import {
  ArrowsOut,
  CircleNotch,
  CirclesThreePlus,
  Code,
  List,
  Planet,
  RocketLaunch,
  SpeakerHigh,
  SpeakerSlash,
  Stop,
  X,
} from "@phosphor-icons/react";
import { AnimatePresence, motion } from "motion/react";
import type {
  DrawRuntime,
  PickerEntry,
  PickscapeSettings,
  ThemeId,
} from "../types";
import { CanvasStage } from "./CanvasStage";

interface StageShellProps {
  entries: PickerEntry[];
  totalEntries: number;
  runtime: DrawRuntime;
  settings: PickscapeSettings;
  backstageExpanded: boolean;
  onOpenBackstage: () => void;
  onStart: () => void;
  onStop: () => void;
  onThemeChange: (theme: ThemeId) => void;
  onDurationChange: (seconds: number) => void;
  onSoundToggle: () => void;
  onFullscreen: () => void;
  onCloseResult: () => void;
  onKeepWinner: () => void;
  onRemoveWinner: () => void;
  onDrawAgain: () => void;
}

const themeOptions: Array<{
  id: ThemeId;
  label: string;
  description: string;
  icon: typeof RocketLaunch;
}> = [
  {
    id: "star-voyage",
    label: "Star Voyage",
    description: "Names arrive from deep space",
    icon: RocketLaunch,
  },
  {
    id: "code-rain",
    label: "Matrix",
    description: "A cascading code field locks the result",
    icon: Code,
  },
  {
    id: "kinetic-cloud",
    label: "Kinetic Cloud",
    description: "A living field moves into focus",
    icon: CirclesThreePlus,
  },
  {
    id: "wheel",
    label: "Wheel",
    description: "A precision wheel lands on the frozen winner",
    icon: CircleNotch,
  },
];

function formatCountdown(milliseconds: number): string {
  const seconds = Math.max(0, Math.ceil(milliseconds / 1_000));
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

export function StageShell({
  entries,
  totalEntries,
  runtime,
  settings,
  backstageExpanded,
  onOpenBackstage,
  onStart,
  onStop,
  onThemeChange,
  onDurationChange,
  onSoundToggle,
  onFullscreen,
  onCloseResult,
  onKeepWinner,
  onRemoveWinner,
  onDrawAgain,
}: StageShellProps) {
  const selectedTheme =
    themeOptions.find((theme) => theme.id === settings.theme) ?? themeOptions[0];
  const durationMs = settings.durationSeconds * 1_000;
  const remainingMs = Math.max(0, durationMs - runtime.elapsedMs);
  const progress = runtime.status === "ready" ? 0 : Math.min(1, runtime.elapsedMs / durationMs);
  const displayedEligible =
    runtime.status === "ready" ? entries.length : runtime.eligibleSnapshot.length;
  const canStart = entries.length > 0 && runtime.status === "ready";
  const canStop = runtime.status === "running";
  const interactiveLabel = canStart
    ? `Start ${selectedTheme?.label ?? "draw"}`
    : canStop
      ? "Stop and reveal the winner"
      : "Draw in progress";

  const activateStage = () => {
    if (canStart) onStart();
    else if (canStop) onStop();
  };

  return (
    <main className="stage-shell" id="pickscape-stage">
      <CanvasStage
        entries={runtime.eligibleSnapshot.length > 0 ? runtime.eligibleSnapshot : entries}
        runtime={runtime}
        theme={settings.theme}
        reducedMotion={settings.motionReduced}
      />

      <button
        type="button"
        className="stage-hit-area"
        onClick={activateStage}
        disabled={!canStart && !canStop}
        aria-label={interactiveLabel}
      />

      <header className="stage-toolbar">
        <button
          type="button"
          className="stage-brand"
          onClick={onOpenBackstage}
          aria-label="Open backstage"
          aria-controls="pickscape-backstage"
          aria-expanded={backstageExpanded}
        >
          <span className="brand-mark">
            <Planet size={22} weight="duotone" />
          </span>
          <span>
            <strong>Pickscape</strong>
            <small>Randomness in motion</small>
          </span>
        </button>

        <div className="theme-switcher" aria-label="Selection theme">
          {themeOptions.map((theme) => {
            const Icon = theme.icon;
            return (
              <button
                type="button"
                key={theme.id}
                className={settings.theme === theme.id ? "active" : ""}
                onClick={() => onThemeChange(theme.id)}
                disabled={runtime.status === "running" || runtime.status === "stopping"}
                aria-pressed={settings.theme === theme.id}
                aria-label={theme.label}
                title={theme.description}
              >
                <Icon size={18} />
                <span>{theme.label}</span>
              </button>
            );
          })}
        </div>

        <div className="toolbar-actions">
          <button
            type="button"
            className="icon-button stage-icon"
            onClick={onOpenBackstage}
            aria-label="Open list"
            aria-controls="pickscape-backstage"
            aria-expanded={backstageExpanded}
          >
            <List size={20} />
          </button>
          <button
            type="button"
            className="icon-button stage-icon"
            onClick={onSoundToggle}
            aria-label={settings.soundEnabled ? "Mute sounds" : "Enable sounds"}
          >
            {settings.soundEnabled ? <SpeakerHigh size={20} /> : <SpeakerSlash size={20} />}
          </button>
          <button
            type="button"
            className="icon-button stage-icon"
            onClick={onFullscreen}
            aria-label="Enter presentation mode"
          >
            <ArrowsOut size={20} />
          </button>
        </div>
      </header>

      <section className="stage-context" aria-live="polite">
        <span>{selectedTheme?.label}</span>
        <h1>
          {runtime.status === "ready"
            ? entries.length > 0
              ? "The field is ready"
              : "Build your selection pool"
            : runtime.status === "running"
              ? "The field is moving"
              : runtime.status === "stopping"
                ? "Locking the result"
                : "Selection complete"}
        </h1>
        <p>
          {displayedEligible > 0
            ? `${displayedEligible.toLocaleString()} eligible from ${totalEntries.toLocaleString()} total`
            : "Add names or items in Backstage to begin."}
        </p>
      </section>

      <div className="stage-footer">
        <div className="duration-control">
          <label htmlFor="duration-select">Auto stop</label>
          <select
            id="duration-select"
            value={settings.durationSeconds}
            onChange={(event) => onDurationChange(Number(event.target.value))}
            disabled={runtime.status !== "ready"}
          >
            <option value={10}>10 seconds</option>
            <option value={20}>20 seconds</option>
            <option value={30}>30 seconds</option>
            <option value={45}>45 seconds</option>
            <option value={60}>60 seconds</option>
          </select>
        </div>

        <div className="draw-action-wrap">
          <button
            type="button"
            className={`draw-action ${runtime.status}`}
            onClick={activateStage}
            disabled={!canStart && !canStop}
            style={{ "--draw-progress": `${progress * 360}deg` } as React.CSSProperties}
          >
            <span className="draw-action-inner">
              {canStop ? <Stop size={20} weight="fill" /> : <RocketLaunch size={20} weight="fill" />}
              <strong>
                {canStart ? "Launch draw" : canStop ? "Stop now" : runtime.status === "stopping" ? "Locking" : "Ready"}
              </strong>
              <small>
                {runtime.status === "running"
                  ? formatCountdown(remainingMs)
                  : canStart
                    ? "Click stage or press Enter"
                    : entries.length === 0
                      ? "Add entries first"
                      : "Result shown"}
              </small>
            </span>
          </button>
        </div>

        <div className="fairness-note">
          <strong>Fair draw</strong>
          <span>Winner is fixed securely at launch</span>
        </div>
      </div>

      <AnimatePresence>
        {runtime.status === "revealed" && runtime.winner ? (
          <motion.section
            className="winner-panel"
            initial={{ opacity: 0, x: "-50%", y: 24, scale: 0.96 }}
            animate={{ opacity: 1, x: "-50%", y: 0, scale: 1 }}
            exit={{ opacity: 0, x: "-50%", y: 14, scale: 0.98 }}
            transition={{ duration: 0.38, ease: [0.16, 1, 0.3, 1] }}
            role="dialog"
            aria-modal="true"
            aria-label={`Selected ${runtime.winner.name}`}
          >
            <button
              type="button"
              className="icon-button winner-close"
              onClick={onCloseResult}
              aria-label="Close winner"
            >
              <X size={20} />
            </button>
            <div className="winner-summary">
              <span className="winner-label">Draw receipt</span>
              <div className="winner-receipt">
                <span>{runtime.eligibleSnapshot.length.toLocaleString()} eligible</span>
                <span>{runtime.stopReason === "manual" ? "Manual stop" : "Automatic stop"}</span>
                <span>{settings.repeatMode === "no-repeat" ? "No repeats" : "Repeats allowed"}</span>
              </div>
            </div>
            <div className="winner-actions">
              <button type="button" className="secondary-button" onClick={onKeepWinner}>
                Keep in pool
              </button>
              <button type="button" className="secondary-button" onClick={onRemoveWinner}>
                Remove winner
              </button>
              <button type="button" className="primary-button" onClick={onDrawAgain}>
                Draw again
              </button>
            </div>
          </motion.section>
        ) : null}
      </AnimatePresence>

      <div className="visually-hidden" aria-live="assertive" aria-atomic="true">
        {runtime.status === "revealed" && runtime.winner
          ? `Selected: ${runtime.winner.name}${
              runtime.winner.category ? `, category ${runtime.winner.category}` : ""
            }`
          : ""}
      </div>
    </main>
  );
}
